import {type Point} from '../../../util/plotHelpers/types';

/**
 * This function calculates the minimum and maximum values from an array of points.
 *
 * PERFORMANCE NOTE: This implementation avoids creating intermediate arrays or objects
 * that would cause excess garbage collection. When working with large datasets,
 * creating temporary arrays or using methods like map/filter/reduce can lead to
 * significant memory pressure and trigger frequent garbage collection cycles,
 * which negatively impacts performance. Instead, we use direct iteration with
 * for loops and in-place variable updates to minimize memory allocations.
 */
export function getBoundaryValues(
  points: Point[],
  config: {ignoreInfinity?: boolean}
) {
  if (points.length === 0) {
    return {
      min: undefined,
      max: undefined,
    };
  }

  let min = Infinity;
  let max = -Infinity;
  let foundValidNumber = false;
  let point, y, y0;

  for (let i = 0; i < points.length; i++) {
    point = points[i];

    // Process y value directly without creating unnecessary variables
    y = point.y;
    if (y !== undefined) {
      if (config.ignoreInfinity ? Number.isFinite(y) : !isNaN(y)) {
        foundValidNumber = true;
        if (y < min) {
          min = y;
        }
        if (y > max) {
          max = y;
        }
      }
    }

    // Process y0 value directly
    y0 = point.y0;
    if (y0 !== undefined) {
      if (config.ignoreInfinity ? Number.isFinite(y0) : !isNaN(y0)) {
        foundValidNumber = true;
        if (y0 < min) {
          min = y0;
        }
        if (y0 > max) {
          max = y0;
        }
      }
    }
  }

  // Avoid creating unnecessary object properties by using a direct return
  return foundValidNumber ? {min, max} : {min: undefined, max: undefined};
}
